import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BookOpen, Plus, Search, Trash2, Volume2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useEnglishVocab } from '../../hooks/useEnglishVocab'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import EnglishVocabAddSheet from '../../components/english/EnglishVocabAddSheet'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// State chips — filter the list to a single SRS bucket. 'all' is the
// default; the others map to the four states the vocab row can be in.
// 'suspended' is kept hidden behind the chip rather than promoted to
// a global filter so the default view shows active study material.
const STATE_CHIPS = ['all', 'new', 'learning', 'review', 'suspended']

function VocabCard({ vocab, onRemove, onSpeak }) {
  const { t, i18n } = useTranslation()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const isAr = i18n.language === 'ar'
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap" dir="ltr">
              <h3 className="text-base font-bold text-foreground">{vocab.word}</h3>
              {vocab.pronunciation_ipa && (
                <span className="text-xs text-muted-foreground font-mono">{vocab.pronunciation_ipa}</span>
              )}
              {vocab.part_of_speech && (
                <span className="text-[10px] uppercase tracking-wide text-app-primary-500 font-semibold">
                  {vocab.part_of_speech}
                </span>
              )}
            </div>
            {vocab.definition_en && (
              <p className="text-sm text-foreground mt-1 leading-relaxed" dir="ltr">
                {vocab.definition_en}
              </p>
            )}
            {vocab.definition_ar && (
              <p className="text-sm text-foreground mt-1 leading-relaxed" dir="rtl">
                {vocab.definition_ar}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => onSpeak(vocab.word)}
              aria-label={t('english.vocab.speak')}
              title={t('english.vocab.speak')}
              className="p-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors"
            >
              <Volume2 className="w-4 h-4" />
            </button>
            {confirmingDelete ? (
              <button
                type="button"
                onClick={() => { setConfirmingDelete(false); onRemove(vocab.id) }}
                aria-label={t('english.vocab.confirmDelete')}
                className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                aria-label={t('english.vocab.delete')}
                className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {vocab.context_sentence && (
          <div className="text-xs text-muted-foreground italic border-s-2 border-app-primary-500/30 ps-2" dir="auto">
            <span dir="auto">&ldquo;{vocab.context_sentence}&rdquo;</span>
          </div>
        )}

        {Array.isArray(vocab.examples) && vocab.examples.length > 0 && (
          <ul className="text-xs text-muted-foreground space-y-1 ps-3 list-disc list-outside" dir="ltr">
            {vocab.examples.slice(0, 3).map((ex, i) => (
              <li key={i}>{ex}</li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-2 text-[10px] text-muted-foreground pt-1" dir="auto">
          <span className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border',
            vocab.srs_state === 'new'
              ? 'border-app-primary-500/40 text-app-primary-500'
              : vocab.srs_state === 'review'
                ? 'border-emerald-500/40 text-emerald-500'
                : 'border-input',
          )}>
            {t(`english.vocab.state.${vocab.srs_state}`)}
          </span>
          {vocab.context_source_kind && vocab.context_source_kind !== 'manual' && (
            <span>
              {t(`english.vocab.source.${vocab.context_source_kind}`, { defaultValue: vocab.context_source_kind })}
            </span>
          )}
        </div>
        {/* eslint-disable-next-line no-unused-vars */ isAr ? null : null}
      </CardContent>
    </Card>
  )
}

export function EnglishVocabPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  // Owner gate.
  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const [search, setSearch] = useState('')
  const [state, setState] = useState('all')
  const [addOpen, setAddOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const { filtered, rows, loading, refetch, add, remove } = useEnglishVocab({ search, state })
  const tts = useTextToSpeech()

  const handleAdd = useCallback(async (payload) => {
    setSaving(true)
    try {
      return await add(payload)
    } finally {
      setSaving(false)
    }
  }, [add])

  const handleSpeak = useCallback((word) => {
    if (!tts.supported || !word) return
    tts.play(word, 'en')
  }, [tts])

  const goBack = () => navigate('/english')
  const handleRefresh = useCallback(() => performRefresh([refetch]), [refetch])

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground flex items-center gap-1.5" dir="auto">
              <BookOpen className="w-4 h-4 text-app-primary-500" />
              {t('english.vocab.title')}
            </h1>
            <p className="text-xs text-muted-foreground" dir="auto">
              {t('english.vocab.subtitle', { count: rows.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            aria-label={t('english.vocab.addTitle')}
            className="p-1.5 rounded-md text-app-primary-500 hover:bg-accent transition-colors"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('english.vocab.searchPlaceholder')}
            className="w-full ps-9 pe-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
            dir="auto"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
          {STATE_CHIPS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setState(s)}
              aria-pressed={state === s}
              className={cn(
                'inline-flex items-center px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors',
                state === s
                  ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                  : 'border-input text-muted-foreground hover:bg-accent',
              )}
            >
              {t(`english.vocab.state.${s}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title={rows.length === 0 ? t('english.vocab.empty') : t('english.vocab.emptyFiltered')}
            description={rows.length === 0 ? t('english.vocab.emptyHint') : null}
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((v) => (
              <VocabCard
                key={v.id}
                vocab={v}
                onRemove={remove}
                onSpeak={handleSpeak}
              />
            ))}
          </div>
        )}

        <EnglishVocabAddSheet
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSave={handleAdd}
          saving={saving}
        />
      </div>
    </PullToRefresh>
  )
}
